import { FormEvent, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import {
  getContractorProfile,
  useLazyChangeContractorProfileQuery,
  useLazyGetContractorProfileQuery,
} from "store/api/contractorsApi"
import { useLazyGetCountriesQuery, useLazyGetStatesQuery, useLazyGetZipCodesQuery } from "store/api/dictionaryApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setUserLogo } from "store/tempDataSlise"
import { OldSubscriptionPlanTitlesWithPrefix } from "constants/SubscriptionPlanTitlesWithPrefix"
import { UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import { convertDataToOptions } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { changeContractorAdditionalInfo, onFormValidate, validationInitialState } from "utils/onValidate"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputLogo } from "components/form/InputLogo"
import { InputNew } from "components/form/InputNew"
import { SelectDynamic } from "components/form/SelectDynamic"

import { SubscriptionUpgradeModal } from "../SubscriptionUpgradeModal/SubscriptionUpgradeModal"

import styles from "./index.module.scss"

const inputValuesInitialState: UITypes.AccountAdditionalInfo = {
  companyName: "",
  brandName: "",
  address1: "",
  address2: "",
  zip: [],
  state: [],
  city: [],
  suburb: [],
  country: [],
  schedulerUrl: "",
  url: "",
  logo: "",
}

interface Props {
  onCancel: () => void
}

export const AccountSettingsAdditionalInfo = ({ onCancel }: Props) => {
  const dispatch = useAppDispatch()
  const [triggerGetContractorProfile, { isFetching, data }] = useLazyGetContractorProfileQuery()
  const [triggerChangeContractorProfile, { isFetching: changeContratorIsFetching }] =
    useLazyChangeContractorProfileQuery()
  const [inputValues, setInputValues] = useState({ ...inputValuesInitialState })
  const [error, setError] = useState(validationInitialState)
  const currentZip = useAppSelector(state => state.tempData.currentZip)
  const selectContractorProfile = useMemo(() => getContractorProfile.select(), [])
  const contractorProfile = useAppSelector(selectContractorProfile)
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState<SubscriptionPlan | null>(null)
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>()
  const addressLabels = getAddressLabelsByCountry(inputValues.country?.[0]?.label)

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const sendRequest = async () => {
    const formData = new FormData()
    formData.append("additional[company_name]", inputValues.companyName)
    formData.append("additional[brand_name]", inputValues.brandName)
    formData.append("additional[address1]", inputValues.address1)
    formData.append("additional[address2]", inputValues.address2)
    formData.append("additional[scheduler_url]", inputValues.schedulerUrl)
    formData.append("additional[url]", inputValues.url)
    if (inputValues?.zip) {
      formData.append("additional[zip]", inputValues.zip[0]?.value as string)
    }
    if (inputValues?.city) {
      formData.append("additional[city]", inputValues.city[0]?.value as string)
    }
    if (inputValues?.state) {
      formData.append("additional[state]", inputValues.state[0]?.value as string)
    }
    if (inputValues?.country) {
      formData.append("additional[country]", inputValues.country[0]?.value as string)
    }
    if (inputValues?.suburb[0]?.value) {
      formData.append("additional[suburb]", inputValues.suburb[0].value as string)
    }

    if (imageFile) {
      formData.append("additional[logo]", imageFile)
    }

    try {
      const res = await triggerChangeContractorProfile(formData)

      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("Account has been successfully updated!")
        triggerGetContractorProfile()
        if (imageFile) {
          dispatch(
            setUserLogo((res?.data as unknown as { data: { additional: { logo: string } } }).data.additional.logo),
          )
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onSubmit = (event: FormEvent<HTMLElement>) => {
    event.preventDefault()

    if (onFormValidate(inputValues, changeContractorAdditionalInfo, setError)) {
      sendRequest()
    }
  }

  // FILL IN CITY AND STATE SELECT BASED ON ZIP
  useEffect(() => {
    if (currentZip?.city) {
      const city = convertDataToOptions({ data: [currentZip.city] }, "id", "name")
      const state = convertDataToOptions({ data: [currentZip.state] }, "id", "name")
      const country = convertDataToOptions({ data: [currentZip.country] }, "id", "name")
      const suburb = convertDataToOptions(
        { data: currentZip.suburbs?.length > 0 ? [currentZip.suburbs[0]] : null },
        "id",
        "name",
      )

      if (city && state) {
        setInputValues({
          ...inputValues,
          ...{
            city,
            state,
            country,
            suburb,
          },
        })
      }
    }
  }, [currentZip])

  useEffect(() => {
    const res = contractorProfile?.data ? contractorProfile?.data : data

    if (res) {
      setInputValues({
        companyName: res?.data?.additional?.company_name || "",
        brandName: res?.data?.additional?.brand_name || "",
        address1: res?.data?.additional?.address1 || "",
        address2: res?.data?.additional?.address2 || "",
        zip: res?.data?.additional?.zip
          ? convertDataToOptions({ data: [res?.data?.additional?.zip] }, "id", "code")
          : [],
        city: res?.data?.additional?.city
          ? convertDataToOptions({ data: [res?.data?.additional?.city] }, "id", "name")
          : [],
        state: res?.data?.additional?.state
          ? convertDataToOptions({ data: [res?.data?.additional?.state] }, "id", "name")
          : [],
        country: res?.data?.additional?.country
          ? convertDataToOptions({ data: [res?.data?.additional?.country] }, "id", "name")
          : [],
        suburb: res?.data?.additional?.suburb
          ? convertDataToOptions({ data: [res?.data?.additional?.suburb] }, "id", "name")
          : [],
        schedulerUrl: res?.data?.additional?.scheduler_url || "",
        url: res?.data?.additional?.url || "",
        logo: res?.data?.additional?.logo || "",
      })
      setCurrentSubscriptionPlan(res?.data?.profile?.subscription_plan || null)
    } else {
      const fetchData = async () => {
        try {
          await triggerGetContractorProfile()
        } catch (error) {
          console.error(error)
        }
      }
      fetchData()
    }
  }, [data, contractorProfile?.data])

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (inputValues.country?.[0]?.value === country[0]?.value) {
      return
    }

    setInputValues({ ...inputValues, ...{ country, zip: [], state: [], city: [], suburb: [] } })
  }

  const renderCitySuburb = () => {
    const showSuburb = inputValues.country?.[0]?.value === 3
    const entity = showSuburb ? "suburb" : "city"

    return (
      <CitySuburbSelect
        showSuburb={showSuburb}
        zip_code={inputValues.zip?.[0]?.label || ""}
        country={inputValues.country?.[0]?.label || ""}
        onChange={value => setInputValues({ ...inputValues, ...{ [entity]: value } })}
        values={inputValues[entity]}
        error={error.field === entity ? error.message : ""}
      />
    )
  }

  return (
    <>
      {showSubscriptionPlans &&
        createPortal(
          <SubscriptionUpgradeModal onCancel={() => setShowSubscriptionPlans(false)} hideUnavailableModal />,
          document.body,
        )}
      <div
        className={classNames({
          [styles.hidden]: showSubscriptionPlans,
        })}
      >
        <div className={classNames(styles.modalContent, styles.settings)}>
          <div className={styles.subscriptionPlanContainer}>
            <label>Current plan</label>
            <div>
              <label>{OldSubscriptionPlanTitlesWithPrefix[currentSubscriptionPlan || SubscriptionPlan.BASIC]}</label>
              <div onClick={() => setShowSubscriptionPlans(true)}>View Plans</div>
            </div>
          </div>
          <InputLogo
            name="logo"
            id="logo"
            value={imageFile && [imageFile]}
            imageUrl={inputValues.logo}
            errorMessage={error.field === "logo" ? error.message : ""}
            label="Upload your logo"
            formatNote="(Format: Png Ratio: 2:1 Max size: 750x375px)"
            onSetFile={files => {
              setError({ field: "logo", message: "" })
              setImageFile(files ? files[0] : null)
            }}
            onValidationError={errorMessage => {
              setError({ field: "logo", message: errorMessage })
            }}
          />
          {isFetching && (
            <div className={styles.spinnerWrapper}>
              <Spinner />
            </div>
          )}
          <div className={styles.formCellsWrapper}>
            <div className={styles.formCell}>
              <InputNew
                label="Company name*"
                placeholder="Company name"
                name="companyName"
                value={inputValues.companyName}
                onChange={onInputChange}
                errorMessage={error.field === "companyName" ? error.message : ""}
              />
            </div>
            <div className={styles.formCell}>
              <InputNew
                label="Brand name*"
                placeholder="Brand name"
                name="brandName"
                value={inputValues.brandName}
                onChange={onInputChange}
                errorMessage={error.field === "brandName" ? error.message : ""}
              />
            </div>
          </div>
          <InputNew
            label="Address 1*"
            placeholder="Enter Address"
            name="address1"
            value={inputValues.address1}
            onChange={onInputChange}
            errorMessage={error.field === "address1" ? error.message : ""}
          />
          <InputNew
            label="Address 2"
            placeholder="Enter Address"
            name="address2"
            value={inputValues.address2}
            onChange={onInputChange}
            errorMessage={error.field === "address2" ? error.message : ""}
          />
          <div className={styles.formCellsWrapper}>
            <div className={styles.formCell}>
              <SelectDynamic
                label="Country"
                searchFieldName="country"
                valueName="id"
                labelName="name"
                name="country"
                values={inputValues.country}
                className={styles.inputText}
                useLazyQuery={useLazyGetCountriesQuery}
                placeholder="Enter Country"
                onChange={value => handleCountryChange(value)}
                errorMessage={error.field === "country" ? error.message : ""}
              />
            </div>
            <div className={styles.formCell}>
              <SelectDynamic
                label={addressLabels.zip}
                valueName="id"
                labelName="code"
                searchFieldName="code"
                name="zip"
                placeholder={`Enter ${addressLabels.zip}`}
                values={inputValues.zip}
                useLazyQuery={useLazyGetZipCodesQuery}
                onChange={value => setInputValues({ ...inputValues, ...{ zip: value } })}
                errorMessage={error.field === "zip" ? error.message : ""}
                queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
              />
            </div>
          </div>
          <div className={styles.formCellsWrapper}>
            <div className={styles.formCell}>
              <SelectDynamic
                label={addressLabels.state}
                valueName="id"
                labelName="name"
                placeholder={`Enter ${addressLabels.state}`}
                name="state"
                useLazyQuery={useLazyGetStatesQuery}
                values={inputValues.state}
                onChange={value => {
                  setInputValues({ ...inputValues, ...{ state: value } })
                }}
                queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
                errorMessage={error.field === "state" ? error.message : ""}
              />
            </div>
            <div className={styles.formCell}>{renderCitySuburb()}</div>
          </div>
          <InputNew
            label="Web scheduler page URL*"
            placeholder="Web scheduler page URL"
            name="schedulerUrl"
            value={inputValues.schedulerUrl}
            onChange={onInputChange}
            errorMessage={error.field === "schedulerUrl" ? error.message : ""}
          />
          <InputNew
            label="URL*"
            placeholder="URL"
            name="url"
            value={inputValues.url}
            onChange={onInputChange}
            errorMessage={error.field === "url" ? error.message : ""}
          />
        </div>
        <footer className={classNames(styles.modalFooter, styles.wide)}>
          <ButtonNew onClick={onCancel} size="halfWidth" color="greyBordered">
            Cancel
          </ButtonNew>
          <ButtonNew
            onClick={onSubmit}
            size="halfWidth"
            type="submit"
            color="blue"
            disabled={changeContratorIsFetching}
          >
            {changeContratorIsFetching ? <Spinner /> : "Save"}
          </ButtonNew>
        </footer>
      </div>
    </>
  )
}
